import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ButtonBase } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemRoundedCheckbox } from "../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { FilterSidebar } from "../../../components/UIToolkit/Filters/FilterSidebar";
import { INK, MOTION, NEUTRAL } from "../../../theme/tokens";

/**
 * Fixed group render order (skip groups absent from `roleGroups`). Keys mirror the backend
 * `roleGroups` keys; each maps to a translation key under `pbj_model.audit.hours_by_role.groups.*`
 * so no English lives in JSX.
 */
export const ROLE_GROUP_ORDER = ["NURSING", "THERAPY", "DIETARY", "ADMINISTRATIVE", "SUPPORT_AND_OTHER"] as const;
export type RoleGroupKey = (typeof ROLE_GROUP_ORDER)[number];

const GROUP_LABEL_KEY: Record<RoleGroupKey, string> = {
	NURSING: "pbj_model.audit.hours_by_role.groups.nursing",
	THERAPY: "pbj_model.audit.hours_by_role.groups.therapy",
	DIETARY: "pbj_model.audit.hours_by_role.groups.dietary",
	ADMINISTRATIVE: "pbj_model.audit.hours_by_role.groups.administrative",
	SUPPORT_AND_OTHER: "pbj_model.audit.hours_by_role.groups.support_other",
};

const COLOR_DOT_SIZE = 8;

/** id-slug so arbitrary role labels ("Registered Nurse (RN)") produce stable kebab-case test ids. */
function toTestIdSlug(id: string): string {
	return id
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

type Props = {
	/** Group key -> role labels present in that group (already ordered by the caller). */
	roleGroups: Record<string, string[]>;
	/** All role labels across every group, in the same stable order used for colors. */
	allRoles: string[];
	/** Stable role -> chart line color (must match the chart's line colors). */
	roleColor: Record<string, string>;
	/** Optional per-role total hours, shown as the row's secondary. */
	roleTotalHours?: Record<string, number>;
	/** Currently hidden roles. checked = visible = NOT in this set. */
	hiddenRoles: ReadonlySet<string>;
	/** Show/hide a single role. */
	onToggleRole: (role: string, visible: boolean) => void;
	/** Show/hide every role in a group at once. */
	onToggleGroup: (groupKey: string, visible: boolean) => void;
	/** Select-all across every role. */
	onSelectAll: () => void;
	/** Clear-all (hide every role) — backs FilterSidebar's onClearAll. */
	onClearAll: () => void;
	collapsed: boolean;
	onCollapsedChange: (collapsed: boolean) => void;
	dataTestId?: string;
};

/**
 * Grouped role-filter tree beside the "Hours by Role" chart. Replaces the chart legend: each row's
 * color dot mirrors the chart line color, and its checkbox drives controlled line visibility. Composes
 * the generic `FilterSidebar` + `SystemRoundedCheckbox` (tri-state) — no fork of `FacetFilterSection`,
 * because that primitive has no group-level select-all row, which is the core control here.
 */
export const RoleFilterPanel: FC<Props> = ({
	roleGroups,
	allRoles,
	roleColor,
	roleTotalHours,
	hiddenRoles,
	onToggleRole,
	onToggleGroup,
	onSelectAll,
	onClearAll,
	collapsed,
	onCollapsedChange,
	dataTestId,
}) => {
	const { t } = useTranslation();

	const visibleRoleCount = allRoles.filter((r) => !hiddenRoles.has(r)).length;
	const allVisible = visibleRoleCount === allRoles.length;
	const someVisible = visibleRoleCount > 0 && !allVisible;
	// "Active filters" = how many roles are hidden; drives FilterSidebar's Clear-all + collapsed pill.
	const activeFilterCount = allRoles.length - visibleRoleCount;

	const orderedGroups = ROLE_GROUP_ORDER.filter((key) => (roleGroups[key]?.length ?? 0) > 0);

	return (
		<FilterSidebar
			collapsed={collapsed}
			onCollapsedChange={onCollapsedChange}
			activeFilterCount={activeFilterCount}
			onClearAll={onClearAll}
			title={t("pbj_model.audit.hours_by_role.filter_title")}
			dataTestId={dataTestId}
			topSlot={
				<SystemRoundedCheckbox
					checked={allVisible}
					indeterminate={someVisible}
					onChange={(next) => (next ? onSelectAll() : onClearAll())}
					label={t("pbj_model.audit.hours_by_role.all_roles")}
					fullWidth
					data-testid={dataTestId ? `${dataTestId}-all-roles` : undefined}
				/>
			}
		>
			{orderedGroups.map((groupKey) => {
				const roles = roleGroups[groupKey];
				const groupVisibleCount = roles.filter((r) => !hiddenRoles.has(r)).length;
				const groupAllVisible = groupVisibleCount === roles.length;
				const groupSomeVisible = groupVisibleCount > 0 && !groupAllVisible;
				return (
					<RoleFilterGroup
						key={groupKey}
						groupKey={groupKey}
						title={t(GROUP_LABEL_KEY[groupKey])}
						roles={roles}
						roleColor={roleColor}
						roleTotalHours={roleTotalHours}
						hiddenRoles={hiddenRoles}
						groupChecked={groupAllVisible}
						groupIndeterminate={groupSomeVisible}
						onToggleRole={onToggleRole}
						onToggleGroup={onToggleGroup}
						dataTestId={dataTestId ? `${dataTestId}-group-${toTestIdSlug(groupKey)}` : undefined}
					/>
				);
			})}
		</FilterSidebar>
	);
};

type GroupProps = {
	groupKey: string;
	title: string;
	roles: string[];
	roleColor: Record<string, string>;
	roleTotalHours?: Record<string, number>;
	hiddenRoles: ReadonlySet<string>;
	groupChecked: boolean;
	groupIndeterminate: boolean;
	onToggleRole: (role: string, visible: boolean) => void;
	onToggleGroup: (groupKey: string, visible: boolean) => void;
	dataTestId?: string;
};

/** One collapsible group: a tri-state group-level checkbox header + one color-dot row per role. */
const RoleFilterGroup: FC<GroupProps> = ({
	groupKey,
	title,
	roles,
	roleColor,
	roleTotalHours,
	hiddenRoles,
	groupChecked,
	groupIndeterminate,
	onToggleRole,
	onToggleGroup,
	dataTestId,
}) => {
	const [expanded, setExpanded] = useState(true);

	return (
		<Box role="group" data-testid={dataTestId}>
			<Box sx={{ display: "flex", alignItems: "center", py: 0.75, px: 0.5 }}>
				<SystemRoundedCheckbox
					checked={groupChecked}
					indeterminate={groupIndeterminate}
					onChange={(next) => onToggleGroup(groupKey, next)}
					label={title}
					fullWidth
					sx={{ flex: 1, "& .MuiTypography-root": { fontWeight: 600, color: INK.PRIMARY } }}
					data-testid={dataTestId ? `${dataTestId}-header-checkbox` : undefined}
				/>
				<ButtonBase
					onClick={() => setExpanded((prev) => !prev)}
					aria-expanded={expanded}
					aria-label={title}
					data-testid={dataTestId ? `${dataTestId}-expand` : undefined}
					sx={{ borderRadius: "6px", p: 0.25 }}
				>
					<ExpandMoreIcon
						sx={{
							fontSize: 20,
							color: INK.MUTED,
							transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
							transition: `transform ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
							"@media (prefers-reduced-motion: reduce)": { transition: "none" },
						}}
					/>
				</ButtonBase>
			</Box>

			{expanded && (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						pb: 0.5,
						pl: 2,
						"@keyframes roleGroupFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
						animation: `roleGroupFadeIn ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
						"@media (prefers-reduced-motion: reduce)": { animation: "none" },
					}}
				>
					{roles.map((role) => {
						const total = roleTotalHours?.[role];
						return (
							<SystemRoundedCheckbox
								key={role}
								checked={!hiddenRoles.has(role)}
								onChange={(next) => onToggleRole(role, next)}
								label={role}
								fullWidth
								secondary={total !== undefined ? `${total.toFixed(0)}h` : undefined}
								startAdornment={<Box sx={{ width: COLOR_DOT_SIZE, height: COLOR_DOT_SIZE, borderRadius: "50%", flexShrink: 0, bgcolor: roleColor[role] }} />}
								data-testid={dataTestId ? `${dataTestId}-role-${toTestIdSlug(role)}` : undefined}
								sx={{
									py: 0.375,
									borderRadius: "6px",
									"@media (hover: hover) and (pointer: fine)": { "&:hover": { backgroundColor: NEUTRAL[50] } },
								}}
							/>
						);
					})}
				</Box>
			)}
		</Box>
	);
};
